import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries } from "lightweight-charts";
import { API } from "@/lib/api";
import { useLang } from "@/i18n";

const INDICATORS = [
  { key: "sma20", label: "SMA 20", color: "#fbbf24" },
  { key: "sma50", label: "SMA 50", color: "#06b6d4" },
  { key: "ema20", label: "EMA 20", color: "#818cf8" },
  { key: "bb", label: "Bollinger (20, 2)", color: "#94a3b8" },
  { key: "vol", label: "Volume", color: "#475569" },
];

function smaSeries(candles, n) {
  const out = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= n) sum -= candles[i - n].close;
    if (i >= n - 1) out.push({ time: candles[i].time, value: sum / n });
  }
  return out;
}

function emaSeries(candles, n) {
  const out = [];
  const k = 2 / (n + 1);
  let prev = null;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i].close;
    prev = prev === null ? c : c * k + prev * (1 - k);
    if (i >= n - 1) out.push({ time: candles[i].time, value: prev });
  }
  return out;
}

function bollingerSeries(candles, n = 20, mult = 2) {
  const upper = [], mid = [], lower = [];
  for (let i = n - 1; i < candles.length; i++) {
    const slice = candles.slice(i - n + 1, i + 1).map((c) => c.close);
    const mean = slice.reduce((a, b) => a + b, 0) / n;
    const sd = Math.sqrt(slice.reduce((a, b) => a + (b - mean) ** 2, 0) / n);
    upper.push({ time: candles[i].time, value: mean + mult * sd });
    mid.push({ time: candles[i].time, value: mean });
    lower.push({ time: candles[i].time, value: mean - mult * sd });
  }
  return { upper, mid, lower };
}

// Binance-grade candlestick chart with technical indicators, backed by live Binance klines.
export default function CandleChart({ symbol, interval }) {
  const containerRef = useRef(null);
  const { t } = useLang();
  const [enabled, setEnabled] = useState({ sma20: true, sma50: false, ema20: false, bb: false, vol: true });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const chart = createChart(el, {
      autoSize: true,
      localization: { locale: "en-US", dateFormat: "dd MMM yyyy" },
      layout: {
        background: { color: "transparent" },
        textColor: "#425466",
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(10,37,64,0.06)" },
        horzLines: { color: "rgba(10,37,64,0.06)" },
      },
      crosshair: {
        vertLine: { color: "rgba(99,91,255,0.4)", labelBackgroundColor: "#635BFF" },
        horzLine: { color: "rgba(99,91,255,0.4)", labelBackgroundColor: "#635BFF" },
      },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: "rgba(10,37,64,0.15)" },
      rightPriceScale: { borderColor: "rgba(10,37,64,0.15)" },
    });
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#10b981", downColor: "#ef4444",
      wickUpColor: "#10b981", wickDownColor: "#ef4444", borderVisible: false,
    });

    const lines = {};
    const mkLine = (color, dashed = false) =>
      chart.addSeries(LineSeries, {
        color, lineWidth: 1, lineStyle: dashed ? 2 : 0,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
      });
    if (enabled.sma20) lines.sma20 = mkLine("#fbbf24");
    if (enabled.sma50) lines.sma50 = mkLine("#06b6d4");
    if (enabled.ema20) lines.ema20 = mkLine("#818cf8");
    if (enabled.bb) {
      lines.bbUpper = mkLine("rgba(148,163,184,0.7)", true);
      lines.bbMid = mkLine("rgba(251,191,36,0.6)", true);
      lines.bbLower = mkLine("rgba(148,163,184,0.7)", true);
    }
    let volSeries = null;
    if (enabled.vol) {
      volSeries = chart.addSeries(HistogramSeries, {
        priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false, priceLineVisible: false,
      });
      chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    }

    const render = (candles) => {
      candleSeries.setData(candles);
      if (lines.sma20) lines.sma20.setData(smaSeries(candles, 20));
      if (lines.sma50) lines.sma50.setData(smaSeries(candles, 50));
      if (lines.ema20) lines.ema20.setData(emaSeries(candles, 20));
      if (enabled.bb) {
        const b = bollingerSeries(candles);
        lines.bbUpper.setData(b.upper);
        lines.bbMid.setData(b.mid);
        lines.bbLower.setData(b.lower);
      }
      if (volSeries) {
        volSeries.setData(candles.map((c) => ({
          time: c.time, value: c.volume,
          color: c.close >= c.open ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)",
        })));
      }
    };

    let cancelled = false;
    let candles = [];
    let poller;

    const load = async () => {
      try {
        const r = await fetch(`${API}/market/klines?symbol=${symbol}&interval=${interval}&limit=300`);
        if (!r.ok) return;
        candles = await r.json();
        if (!cancelled) {
          render(candles);
          chart.timeScale().fitContent();
        }
      } catch (e) { /* transient */ }
    };

    const pollLast = async () => {
      try {
        const r = await fetch(`${API}/market/klines?symbol=${symbol}&interval=${interval}&limit=2`);
        if (!r.ok || cancelled || !candles.length) return;
        const fresh = await r.json();
        for (const k of fresh) {
          const idx = candles.findIndex((c) => c.time === k.time);
          if (idx >= 0) candles[idx] = k;
          else if (k.time > candles[candles.length - 1].time) candles.push(k);
        }
        render(candles);
      } catch (e) { /* transient */ }
    };

    load();
    poller = setInterval(pollLast, 5000);

    return () => {
      cancelled = true;
      clearInterval(poller);
      chart.remove();
    };
  }, [symbol, interval, enabled]);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="indicator-toolbar">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mr-1">{t("chart.indicators")}</span>
        {INDICATORS.map((ind) => (
          <button
            key={ind.key}
            data-testid={`ind-toggle-${ind.key}`}
            onClick={() => setEnabled((p) => ({ ...p, [ind.key]: !p[ind.key] }))}
            className={`flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-ticker border transition-colors ${
              enabled[ind.key]
                ? "border-[#635BFF]/50 bg-[#635BFF]/10 text-[#0A2540]"
                : "border-[#E6EBF1] text-slate-400 hover:text-[#425466] hover:bg-[#F6F9FC]"
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: ind.color }} />
            {ind.label}
          </button>
        ))}
      </div>
      <div ref={containerRef} data-testid="candle-chart" className="w-full h-[440px]" />
    </div>
  );
}
